import { useEffect, useRef } from 'react';

/**
 * Clears selected storage keys when the user leaves the page (unmount or beforeunload).
 * Pass `{ enabled: false }` to keep the stored state (e.g. "Remember my search and filters").
 * Preferencing is evaluated at clear-time so toggling On mid-session does not wipe current state.
 */
const useClearStorageOnLeave = (
    localStorageKeys = [],
    sessionStorageKeys = [],
    { enabled = true } = {}
) => {
    const localKeysRef = useRef(localStorageKeys);
    const sessionKeysRef = useRef(sessionStorageKeys);
    const enabledRef = useRef(enabled);
    // update the refs to the latest values
    localKeysRef.current = localStorageKeys;
    sessionKeysRef.current = sessionStorageKeys;
    enabledRef.current = enabled;

    useEffect(() => {
        const clearAll = () => {
            if (!enabledRef.current) return;

            localKeysRef.current.forEach((key) => {
                try {
                    window.localStorage.removeItem(key);
                } catch {
                    /* empty */
                }
            });
            sessionKeysRef.current.forEach((key) => {
                try {
                    window.sessionStorage.removeItem(key);
                } catch {
                    /* empty */
                }
            });
        };

        window.addEventListener('beforeunload', clearAll);
        return () => {
            window.removeEventListener('beforeunload', clearAll);
            clearAll();
        };
    }, []);
};

export default useClearStorageOnLeave;
